import { createFileRoute, notFound } from "@tanstack/react-router";
import { z } from "zod";
import { i18nLinks, i18nOgMeta, localeUrl } from "@/lib/seo";
import { seasonsQueryOptions } from "@/tools/f1-fantasy/hooks/use-f1-data";
import { constructorProfileQueryOptions } from "@/tools/f1-fantasy/hooks/use-statistics";
import { DEFAULT_SEASON } from "@/tools/f1-fantasy/lib/constants";
import { ConstructorProfile } from "@/tools/f1-fantasy/pages/ConstructorProfile";

export const Route = createFileRoute(
  "/f1-fantasy/statistics/constructors/$slug",
)({
  validateSearch: z.object({
    season: z.coerce.number().optional(),
    context: z.enum(["race", "fantasy"]).optional(),
  }),
  head: (ctx) => {
    // TanStack Router infers params correctly for $slug routes at runtime;
    // the type assertion is needed because the generic inference breaks when
    // head() is combined with loaderDeps + loader in the same route config.
    const slug = (ctx.params as { slug: string }).slug;
    const name = slug
      .split("-")
      .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ");
    const basePath = `/f1-fantasy/statistics/constructors/${slug}`;
    const canonicalUrl = `https://toolverse.me${basePath}`;
    return {
      meta: [
        // ponytail: noindex until these pages carry real editorial content —
        // see drivers.$slug.tsx for rationale.
        { name: "robots", content: "noindex, follow" },
        {
          title: `${name} — F1 Constructor Stats | Toolverse`,
        },
        {
          name: "description",
          content: `${name} F1 constructor statistics, fantasy points, driver performance, and race-by-race results across all seasons.`,
        },
        {
          property: "og:title",
          content: `${name} — F1 Constructor Stats | Toolverse`,
        },
        ...i18nOgMeta(basePath),
      ],
      links: [...i18nLinks(basePath)],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SportsTeam",
            name,
            sport: "Formula 1",
            url: canonicalUrl,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              {
                "@type": "ListItem",
                position: 1,
                name: "Home",
                item: localeUrl("/"),
              },
              {
                "@type": "ListItem",
                position: 2,
                name: "F1 Fantasy",
                item: localeUrl("/f1-fantasy"),
              },
              {
                "@type": "ListItem",
                position: 3,
                name: "Statistics",
                item: localeUrl("/f1-fantasy/statistics"),
              },
              {
                "@type": "ListItem",
                position: 4,
                name,
                item: canonicalUrl,
              },
            ],
          }),
        },
      ],
    };
  },
  component: ConstructorProfile,
  ssr: true,
  loaderDeps: ({ search }) => ({
    season: search.season,
    context: search.context,
  }),
  // @ts-expect-error — TanStack Router infers loader return as never when head()
  // uses ctx.params in a $slug route with loaderDeps; runtime behavior is correct.
  loader: async ({ context, params, deps }) => {
    const seasons = await context.queryClient.ensureQueryData(
      seasonsQueryOptions(),
    );
    const latestSeason = seasons?.[0]?.year ?? DEFAULT_SEASON;
    const season = (deps as { season?: number }).season ?? latestSeason;

    const profile = await context.queryClient.ensureQueryData(
      constructorProfileQueryOptions(params.slug, season),
    );
    // Real 404 for unknown constructor slugs — soft 404s let Google index/crawl
    // literal template URLs like /f1-fantasy/statistics/constructors/$slug
    if (!profile) throw notFound();

    return { season };
  },
});
